// Types
import {
  type MapboxForwardResponse,
  AddressSuggestionsResponse,
} from '@shared/types';

// Services

// Constants
import { MAPBOX_MESSAGE } from '@shared/constants';
import { END_POINTS } from '@/constants/endpoint';

// Configs
import ENV from '@shared/configs';

// Types
import { HttpMethod } from '@/types/api';
import { httpClient } from '@/services/http';
import { RequestConfig } from '@/types';
import { Effect, pipe } from 'effect';

export const getAddressSuggestions = async (
  search: string,
  requestConfig?: RequestConfig,
): Promise<AddressSuggestionsResponse> =>
  Effect.runPromise(
    pipe(
      Effect.tryPromise({
        try: async () => {
          if (!ENV.MAPBOX_ACCESS_TOKEN) {
            throw new Error(MAPBOX_MESSAGE.NO_ACCESS_TOKEN);
          }

          const { data, error } = await httpClient<MapboxForwardResponse>(
            END_POINTS.MAPBOX_FORWARD_GEOCODING(
              search,
              ENV.MAPBOX_ACCESS_TOKEN,
            ),
            {
              method: HttpMethod.GET,
              ...requestConfig,
            },
            true,
            true,
          );

          if (error) {
            throw new Error(error);
          }

          if (!data) {
            return {
              data: null,
              error: MAPBOX_MESSAGE.NO_DATA,
            };
          }

          const MapboxDatas = data.features.map((feature) => {
            const { properties, geometry } = feature;
            const {
              name = '',
              full_address = '',
              place_formatted = '',
              address = '',
              mapbox_id = '',
              feature_type,
            } = properties || {};

            const displayAddress = full_address || place_formatted || address;

            return {
              id: mapbox_id,
              text: name,
              place_name: displayAddress,
              center: geometry?.coordinates || [],
              feature_type,
            };
          });

          return {
            data: MapboxDatas,
            error: null,
          };
        },
        catch: (error) => ({
          data: null,
          error:
            error instanceof Error
              ? error.message
              : MAPBOX_MESSAGE.FAILED_TO_FETCH_SUGGESTIONS,
        }),
      }),
      Effect.catchAll((error) => Effect.succeed(error)),
    ),
  );
